import Papa from 'papaparse';

import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';

import { makeStyles } from '@mui/styles';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Theme } from '@mui/material/styles';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import ProductLayout from '../../../layout/Product';
import StickyFormFooter from '../../../components/_form/StickyFormFooter';
import useNotification from '../../../components/_hook/useNotification';
import useRouter from '../../../components/_hook/useRouter';
import useLanguage from '../../../components/_hook/useLanguage';
import { useProductLazyQuery } from '../../../../graphql/query/product/useProductQuery';
import { IProductSchema } from '../../../../graphql/type/product';
import { useUploadProductImagesMutation } from '../../../../graphql/mutation/product/productImage/uploadProductImagesMutation';
import { useDeleteAllProductImagesMutation } from '../../../../graphql/mutation/product/productImage/deleteAllProductImagesMutation';
import Helmet from '../../../components/Helmet';
import plan from '../../../components/util/plan';
import Context from '../../../context';

const useStyles = makeStyles<Theme>((theme) => ({
  paper: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  inputFile: { display: 'none !important' },
  resultImage: {
    maxHeight: '50px',
    margin: '2px 2px',
    cursor: 'grab',
  },
  paperInfo: {
    padding: theme.spacing(1, 1),
    backgroundColor: theme.palette.primary.main,
    color: 'white',
  },
  tableWrapper: { overflowX: 'auto' },
  buttonDanger: {
    backgroundColor: '#F92949',
    color: '#fff',
    '&:hover': { backgroundColor: '#e80729' },
  },
}));

let isPauseUploading = false;

function ProductImportImage() {
  const classes = useStyles();
  const { t } = useTranslation();
  const { params } = useRouter();
  const { getText } = useLanguage();
  const { notification } = useNotification();
  const { company } = useContext(Context);
  const { companyID } = params;
  const [isUploading, setIsUploading] = useState(false);
  const [isMatchingProduct, setIsMatchingProduct] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [relatedProducts, setRelatedProducts] = useState<IProductSchema[]>([]);
  const [matchedResults, setMatchedResults] = useState(new Map());
  const [productFilenameMapping, setProductFilenameMapping] = useState(
    new Map()
  );
  const [queryVariableProductNames, setQueryVariableProductNames] = useState<
    string[]
  >([]);
  const [
    queryVariableProductNameIndex,
    setQueryVariableProductNameIndex,
  ] = useState<number>(0);
  const [
    isFetchingRelatedProduct,
    setIsFetchingRelatedProduct,
  ] = useState<boolean>(false);

  const isFreePackage = plan.isFreePackage(company.planData);

  const productNameCSVHeaderKey = 'Product Name';
  const fileNameCSVHeaderKey = 'File Name';

  function updateMatchedResults(k: string, v: any) {
    setMatchedResults(new Map(matchedResults.set(k, v)));
  }

  function updateProductFilenameMapping(k: string, v: any) {
    setProductFilenameMapping(new Map(productFilenameMapping.set(k, v)));
  }

  const [uploadProductImagesMutation] = useUploadProductImagesMutation(
    `updatedProduct {
				_id
                media {
                    images {
                        status
                        filename
                        filename_large

                        filenameUrl
                        filenameLargeUrl
                    }
                }
			}`,
    {
      onCompleted: (data) => {
        if (data && data.uploadProductImages) {
          const { updatedProduct } = data.uploadProductImages;

          if (updatedProduct) {
            const matchedResult = matchedResults.get(updatedProduct._id);

            if (matchedResult && updatedProduct._id) {
              matchedResult.isUploading = false;
              matchedResult.isCompleted = true;
              updateMatchedResults(updatedProduct._id, matchedResult);
            }
          }
        }
      },
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );

  const [deleteAllProductImagesMutation] = useDeleteAllProductImagesMutation(
    `updatedProducts {
				_id
                media {
                    images {
                        status
                        filename
                        filename_large

                        filenameUrl
                        filenameLargeUrl
                    }
                }
			}`,
    {
      onCompleted: (data) => {
        if (data && data.deleteAllProductImages) {
          const { updatedProducts } = data.deleteAllProductImages;

          if (updatedProducts) {
            updatedProducts.forEach((updatedProduct) => {
              const matchedResult = matchedResults.get(updatedProduct._id);

              if (matchedResult && updatedProduct._id) {
                matchedResult.isRemovingOldImage = false;
                updateMatchedResults(updatedProduct._id, matchedResult);
              }
            });
          }
        }
      },
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );

  function getProductNameByFilename(filename: string) {
    // if fileName = my-product-1.jpg, get my-product as product name
    const matchResult = filename.match(/-\d+(\.)\w*$/);

    if (matchResult && matchResult.length) {
      return filename.replace(matchResult[0], '');
    }
    return '';
  }

  function matchProductWithSelectedFile(products: IProductSchema[]) {
    setIsMatchingProduct(true);

    const results = new Map();

    products.forEach((product) => {
      const productName = getText(product.name, 'en');

      selectedFiles.forEach((file) => {
        const fileProductName = getProductNameByFilename(file.name);
        const productMapping = productFilenameMapping.get(productName);
        const mappingFileName = productMapping ? productMapping.fileName : '';

        if (
          productName === fileProductName ||
          mappingFileName === fileProductName
        ) {
          const resultData = results.get(product._id) || {
            product,
            files: [],
            isRemoveOldImage: false,
            isEnableUploadImage: true,
            isRemovingOldImage: false,
            isUploading: false,
            isCompleted: false,
          };

          resultData.files.push(file);

          results.set(product._id, resultData);
        }
      });
    });

    setMatchedResults(results);

    setIsMatchingProduct(false);
  }

  const [
    getProducts,
    { data: productData, loading: isProductLoading },
  ] = useProductLazyQuery(
    `
           _id
          company
          name {
            text
            la
          }
      `,
    {
      onCompleted: (data) => {
        if (data && data.products) {
          const products = data.products.items;
          if (queryVariableProductNameIndex === 100) {
            setRelatedProducts(products);
            if (
              queryVariableProductNameIndex >= queryVariableProductNames.length
            ) {
              matchProductWithSelectedFile(products);
            }
          } else {
            const allProducts = [...relatedProducts, ...products];
            setRelatedProducts(allProducts);

            if (
              queryVariableProductNameIndex >= queryVariableProductNames.length
            ) {
              matchProductWithSelectedFile(allProducts);
            }
          }
          setIsFetchingRelatedProduct(false);
        }
      },
      fetchPolicy: 'cache-and-network',
    }
  );

  function fetchRelatedProducts(tempFiles: null | File[]) {
    const files = tempFiles || selectedFiles;
    const mappings = productFilenameMapping;
    const fileNames = files.map((file) => file.name);
    const uniqueProductNames = new Set();

    fileNames.forEach((filename) => {
      const productName = getProductNameByFilename(filename);

      if (productName) {
        uniqueProductNames.add(productName);
      }
    });

    Array.from(mappings.keys()).forEach((productName) => {
      uniqueProductNames.add(productName);
    });
    setQueryVariableProductNames(Array.from(uniqueProductNames) as string[]);
    setQueryVariableProductNameIndex(0);
  }

  useEffect(() => {
    if (
      !isFetchingRelatedProduct &&
      !isProductLoading &&
      queryVariableProductNameIndex < queryVariableProductNames.length
    ) {
      setIsFetchingRelatedProduct(true);
      getProducts({
        variables: {
          filter: {
            companyId: companyID,
            names: queryVariableProductNames.slice(
              queryVariableProductNameIndex,
              queryVariableProductNameIndex + 100
            ),
          },
          limit: 100,
        },
      });
      setQueryVariableProductNameIndex(queryVariableProductNameIndex + 100);
    }
  }, [
    isFetchingRelatedProduct,
    isProductLoading,
    queryVariableProductNameIndex,
    JSON.stringify(queryVariableProductNames),
  ]);

  function onSelectedFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const { files } = e.currentTarget;

    if (files) {
      setSelectedFiles(Array.from(files));
      fetchRelatedProducts(Array.from(files));
    }
  }

  async function removeProductImage(matchedResult: any) {
    matchedResult.isRemovingOldImage = true;
    updateMatchedResults(matchedResult.product._id, matchedResult);
    return deleteAllProductImagesMutation({
      variables: {
        _ids: [matchedResult.product._id],
      },
    });
  }

  async function uploadProductImage(matchedResult: any) {
    matchedResult.isUploading = true;
    updateMatchedResults(matchedResult.product._id, matchedResult);
    return uploadProductImagesMutation({
      variables: {
        _id: matchedResult.product._id,
        images: matchedResult.files,
        status: matchedResult.isEnableUploadImage,
      },
    });
  }

  async function upload() {
    setIsUploading(true);
    isPauseUploading = false;
    const values = Array.from(matchedResults.values());

    // eslint-disable-next-line no-restricted-syntax
    for await (const matchedResult of values) {
      if (isPauseUploading) {
        break;
      }

      if (!matchedResult.isCompleted) {
        if (matchedResult.isRemoveOldImage) {
          await removeProductImage(matchedResult);
        }
        await uploadProductImage(matchedResult);
      }
    }

    setIsUploading(false);
    notification.success(t('upload_completed'));
  }

  function onSelectedCsvFile(evt: React.ChangeEvent<HTMLInputElement>) {
    const { files } = evt.target;
    if (files && files.length) {
      const file = files[0];
      let count = 0;
      const headerIndexObj: any = {};

      if (file && file.name) {
        const mappings = new Map();

        // get csv header
        Papa.parse(file, {
          skipEmptyLines: true,
          step: (row, parser) => {
            const isHeader = count === 0;

            if (row.errors && row.errors[0]) {
              const error = row.errors[0];

              // notification.error(`${t('file_line_parse_error', { line : error.row })} - TYPE: ${error.type}, CODE: ${error.code}, MESSAGE: ${error.message}.`)
            } else {
              const headers = row.data as Record<string, any>;

              if (headers.length > 0) {
                if (isHeader) {
                  headers.forEach((header: string, index: number) => {
                    headerIndexObj[header] = index;
                  });

                  if (headerIndexObj[productNameCSVHeaderKey] === undefined) {
                    notification.error(
                      t('file_no__header__header_found_error', {
                        header: t('product_name'),
                      })
                    );
                  }

                  if (headerIndexObj[fileNameCSVHeaderKey] === undefined) {
                    notification.error(
                      t('file_no__header__header_found_error', {
                        header: t('file_name'),
                      })
                    );
                  }
                } else {
                  const productName =
                    headers[headerIndexObj[productNameCSVHeaderKey]];
                  const fileName =
                    headers[headerIndexObj[fileNameCSVHeaderKey]];

                  mappings.set(productName, {
                    productName,
                    fileName,
                  });
                }
              } else {
                notification.error(t('file_no_header_found_error'));
              }
            }
            count += 1;
          },
          complete: (results) => {
            setProductFilenameMapping(mappings);
            fetchRelatedProducts(null);
          },
        });
      }
    }
  }

  const SortableItem = SortableElement(({ file }: any) => (
    <Tooltip title={file.name}>
      <img
        alt={file.name}
        className={classes.resultImage}
        src={URL.createObjectURL(file)}
      />
    </Tooltip>
  ));
  const SortableList = SortableContainer(({ files }: any) => (
    <div>
      {files.map((file: File, index: number) => (
        <SortableItem key={file.name} index={index} file={file} />
      ))}
    </div>
  ));

  const matchedResultValues = Array.from(matchedResults.values());
  const matchedResultCompleted = matchedResultValues.filter(
    (temp) => temp.isCompleted
  );
  const productFilenameMappingValues = Array.from(
    productFilenameMapping.values()
  );
  const removeOldImageCount = matchedResultValues.filter(
    (matchedResult) => matchedResult.isRemoveOldImage
  ).length;
  const enableUploadImageCount = matchedResultValues.filter(
    (matchedResult) => matchedResult.isEnableUploadImage
  ).length;
  const isUploadCompleted =
    matchedResultValues.filter((matchedResult) => matchedResult.isCompleted)
      .length === matchedResultValues.length;
  const matchedFileCount = matchedResultValues.reduce(
    (count, matchedResult) => count + matchedResult.files.length,
    0
  );
  const noMatchFiles = selectedFiles.filter(
    (selectedFile) =>
      !matchedResultValues.find((matchedResultValue) =>
        matchedResultValue.files.find(
          (file: File) => file.name === selectedFile.name
        )
      )
  );
  const noMatchFileCount = noMatchFiles.length;

  return (
    <Paper className={classes.paper}>
      <Helmet title={`${t('import_image')} | ${t('product')}`} />
      <Grid container item xs={12} spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h6">{t('import_product_image')}</Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle1">
            {t(
              'you_can_mass_import_product_images_to_respectively_product_showcase_here'
            )}
            .
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              <Typography variant="h6">{t('instruction')}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container direction="column">
                <Typography variant="subtitle1" display="block">
                  {t('image_file_name_should_follow_format')}:{' '}
                  <strong>(Product name)-(number)</strong>
                  <br />
                  <br />
                  <strong>{t('example')}:</strong>
                  <br />
                  {t('product_name')}: <strong>MyProduct</strong>
                  <br />
                  {t('image_file_name_should_follow_as')}:<br />
                  1. <strong>MyProduct</strong>-1
                  <br />
                  2. <strong>MyProduct</strong>-2
                  <br />
                  3. <strong>MyProduct</strong>-3
                  <br />
                  <br />
                  {t('product_name')}: <strong>My New Product</strong>
                  <br />
                  {t('image_file_name_should_follow_as')}:<br />
                  1. <strong>My New Product</strong>-1
                  <br />
                  2. <strong>My New Product</strong>-2
                  <br />
                  <br />
                </Typography>
                <br />
                <Typography variant="h5" display="block" align="center">
                  {t('or')}
                </Typography>
                <Typography variant="subtitle1" display="block">
                  {t('you_can_upload_a_csv_mapping_file_as_below_structure')}
                </Typography>
                <Table className={classes.table}>
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">
                        {productNameCSVHeaderKey}
                      </TableCell>
                      <TableCell align="center">
                        {fileNameCSVHeaderKey}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell align="center">
                        <strong>MyProduct</strong>
                      </TableCell>
                      <TableCell align="center">
                        {t(
                          'product_image_file_name_file_name_should_still_end_with_number'
                        )}
                        <br />
                        Example: <strong>MyProductImage</strong>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle1" display="inline">
            {t('please_select_image_to_view_match_result')}&nbsp;&nbsp;
          </Typography>
          <input
            type="file"
            disabled={isFreePackage}
            onChange={onSelectedFiles}
            multiple
            className={classes.inputFile}
            id="upload"
            accept={'image/*'}
            onClick={(event) => {
              // @ts-ignore
              event.target.value = null;
            }}
          />
          <Tooltip
            title={
              isFreePackage
                ? t(
                    'you_are_subscribed_to_free_package_please_upgrade_your_subscription_to_enjoy_more_benefit'
                  )
                : ''
            }
          >
            <label htmlFor="upload">
              <Button color="primary" variant="contained" component="span">
                {t('select_image')}
              </Button>
            </label>
          </Tooltip>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle1" display="inline">
            {t('select_csv_mapping_file_optional')}&nbsp;&nbsp;
          </Typography>
          <input
            type="file"
            disabled={isFreePackage}
            onChange={onSelectedCsvFile}
            className={classes.inputFile}
            id="uploadCsv"
            accept=".csv"
            onClick={(event) => {
              // @ts-ignore
              event.target.value = null;
            }}
          />
          <Tooltip
            title={
              isFreePackage
                ? t(
                    'you_are_subscribed_to_free_package_please_upgrade_your_subscription_to_enjoy_more_benefit'
                  )
                : ''
            }
          >
            <label htmlFor="uploadCsv">
              <Button color="primary" variant="contained" component="span">
                {t('select_csv_mapping_file')}
              </Button>
            </label>
          </Tooltip>
        </Grid>
        {productFilenameMappingValues.length > 0 && (
          <Grid item xs={12}>
            <Accordion
              defaultExpanded
              TransitionProps={{
                timeout: 0,
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
              >
                <Typography variant="h6">
                  {t('product_and_file_name_mapping_result')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Table className={classes.table}>
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">{t('product')}</TableCell>
                      <TableCell align="center">{t('file_name')}</TableCell>
                      <TableCell align="center">{t('example')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {productFilenameMappingValues.map(
                      (productFilenameMappingValue) => (
                        <TableRow key={productFilenameMappingValue.fileName}>
                          <TableCell align="center">
                            <strong>
                              {productFilenameMappingValue.productName}
                            </strong>
                          </TableCell>
                          <TableCell align="center">
                            <strong>
                              {productFilenameMappingValue.fileName}
                            </strong>
                          </TableCell>
                          <TableCell align="center">
                            {[1, 2].map((count) => (
                              <React.Fragment key={count}>
                                <strong>
                                  {productFilenameMappingValue.fileName}-{count}
                                </strong>
                                ,&nbsp;
                              </React.Fragment>
                            ))}
                            ...
                          </TableCell>
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </AccordionDetails>
            </Accordion>
          </Grid>
        )}
        {isUploading ? (
          <Grid container item xs={12} spacing={2} alignItems="center">
            <Grid item>
              <Typography variant="subtitle1" display="inline">
                {t('uploading_image')}&nbsp;
                {matchedResultCompleted.length}&nbsp;/&nbsp;
                {matchedResultValues.length}
              </Typography>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                className={classes.buttonDanger}
                size="small"
                onClick={() => {
                  isPauseUploading = true;
                }}
                startIcon={<StopCircleIcon />}
              >
                {t('stop')} {t('upload')}
              </Button>
            </Grid>
          </Grid>
        ) : (
          <Grid container item xs={12} spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle1">{t('match_result')}</Typography>
            </Grid>
            <Grid container item xs={12} spacing={1}>
              <Grid
                container
                item
                xs={12}
                justifyContent="center"
                direction="column"
              >
                {isMatchingProduct && (
                  <>
                    <Typography
                      align="center"
                      variant="subtitle2"
                      display="inline"
                    >
                      {t('matching_product')}
                    </Typography>
                    <LinearProgress />
                  </>
                )}
                {isProductLoading && (
                  <>
                    <Typography
                      align="center"
                      variant="subtitle2"
                      display="inline"
                    >
                      {t('fetching_product')}
                    </Typography>
                    <LinearProgress />
                  </>
                )}
                {!isMatchingProduct &&
                  !isProductLoading &&
                  selectedFiles.length > 0 && (
                    <Paper className={classes.paperInfo}>
                      <Typography
                        align="center"
                        variant="subtitle1"
                        color="initial"
                      >
                        {t(
                          'you_have_selected__fileCount__file(s), __matchedFileCount__file(s)_matched__productCount__product(s)',
                          {
                            fileCount: selectedFiles.length,
                            productCount: relatedProducts.length,
                            matchedFileCount,
                          }
                        )}
                      </Typography>
                    </Paper>
                  )}
              </Grid>
              {noMatchFileCount > 0 && (
                <Grid item xs={12}>
                  <Accordion>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="panel1a-content"
                      id="panel1a-header"
                    >
                      <Typography variant="h6">
                        {t('__fileCount__file(s)_no_match_with_any_product', {
                          fileCount: noMatchFileCount,
                        })}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container>
                        {noMatchFiles.map((noMatchFile, index) => (
                          <Grid key={noMatchFile.name} item xs={12}>
                            <Typography
                              variant="subtitle1"
                              component="p"
                              display="block"
                            >
                              {index + 1}.&nbsp;{noMatchFile.name}
                            </Typography>
                          </Grid>
                        ))}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              )}
            </Grid>
            <Grid item xs={12} className={classes.tableWrapper}>
              {matchedResults.size > 0 && (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('product')}</TableCell>
                      <TableCell>
                        {t('image', { count: 2 })}&nbsp; (
                        {t('drag_and_drop_to_rearrange_order')})
                      </TableCell>
                      <TableCell align="right">
                        <FormControlLabel
                          control={
                            <Checkbox
                              indeterminate={
                                removeOldImageCount > 0 &&
                                removeOldImageCount < matchedResultValues.length
                              }
                              checked={
                                removeOldImageCount ===
                                matchedResultValues.length
                              }
                              onChange={(event) => {
                                const results = new Map();

                                matchedResultValues.forEach(
                                  (matchedResultValue) => {
                                    matchedResultValue.isRemoveOldImage =
                                      event.target.checked;
                                    results.set(
                                      matchedResultValue.product._id,
                                      matchedResultValue
                                    );
                                  }
                                );
                                setMatchedResults(results);
                              }}
                              color="primary"
                            />
                          }
                          label={
                            <Tooltip
                              title={t(
                                'if_checked, will_remove_all_old_image_before_upload'
                              )}
                            >
                              <Typography variant="subtitle2" display="inline">
                                {t('remove_old_image')}
                              </Typography>
                            </Tooltip>
                          }
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              indeterminate={
                                enableUploadImageCount > 0 &&
                                enableUploadImageCount <
                                  matchedResultValues.length
                              }
                              checked={
                                enableUploadImageCount ===
                                matchedResultValues.length
                              }
                              onChange={(event) => {
                                const results = new Map();

                                matchedResultValues.forEach(
                                  (matchedResultValue) => {
                                    matchedResultValue.isEnableUploadImage =
                                      event.target.checked;
                                    results.set(
                                      matchedResultValue.product._id,
                                      matchedResultValue
                                    );
                                  }
                                );
                                setMatchedResults(results);
                              }}
                              color="primary"
                            />
                          }
                          label={
                            <Tooltip
                              title={t(
                                'if_checked, will_publish_image_after_upload'
                              )}
                            >
                              <Typography variant="subtitle2" display="inline">
                                {t('publish_image_after_upload')}
                              </Typography>
                            </Tooltip>
                          }
                        />
                      </TableCell>
                      <TableCell align="right">{t('status')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {matchedResultValues.map((result) => (
                      <TableRow key={result.product._id}>
                        <TableCell>{getText(result.product.name)}</TableCell>
                        <TableCell>
                          <SortableList
                            onSortEnd={({ oldIndex, newIndex }: any) => {
                              if (oldIndex !== newIndex) {
                                const matchedResult = matchedResults.get(
                                  result.product._id
                                );
                                const files = Array.from(matchedResult.files);
                                const movingFile = files[oldIndex];

                                files.splice(oldIndex, 1);
                                files.splice(newIndex, 0, movingFile);
                                matchedResult.files = files;
                                matchedResult.isCompleted = false;
                                updateMatchedResults(
                                  result.product._id,
                                  matchedResult
                                );
                              }
                            }}
                            files={result.files}
                            axis="xy"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={result.isRemoveOldImage}
                                onChange={() => {
                                  const matchedResult = matchedResults.get(
                                    result.product._id
                                  );

                                  matchedResult.isRemoveOldImage = !matchedResult.isRemoveOldImage;
                                  updateMatchedResults(
                                    result.product._id,
                                    matchedResult
                                  );
                                }}
                                color="primary"
                              />
                            }
                            label={
                              <Tooltip
                                title={t(
                                  'if_checked, will_remove_all_old_image_before_upload'
                                )}
                              >
                                <Typography
                                  variant="subtitle2"
                                  display="inline"
                                >
                                  {t('remove_old_image')}
                                </Typography>
                              </Tooltip>
                            }
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={result.isEnableUploadImage}
                                onChange={() => {
                                  const matchedResult = matchedResults.get(
                                    result.product._id
                                  );

                                  matchedResult.isEnableUploadImage = !matchedResult.isEnableUploadImage;
                                  updateMatchedResults(
                                    result.product._id,
                                    matchedResult
                                  );
                                }}
                                color="primary"
                              />
                            }
                            label={
                              <Tooltip
                                title={t(
                                  'if_checked, will_publish_image_after_upload'
                                )}
                              >
                                <Typography
                                  variant="subtitle2"
                                  display="inline"
                                >
                                  {t('publish_image_after_upload')}
                                </Typography>
                              </Tooltip>
                            }
                          />
                        </TableCell>
                        <TableCell align="right">
                          {result.isRemovingOldImage && t('removing_old_image')}
                          {result.isUploading && t('uploading_image')}
                          {result.isCompleted && t('completed')}
                          {!result.isRemovingOldImage &&
                            !result.isUploading &&
                            !result.isCompleted &&
                            t('ready')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {matchedResults.size === 0 && selectedFiles.length === 0 && (
                <Typography variant="subtitle1" align="center">
                  {t('please_select_image')}
                </Typography>
              )}
              {matchedResults.size === 0 && selectedFiles.length !== 0 && (
                <Typography variant="subtitle1" align="center">
                  {t('no_product_match')}
                </Typography>
              )}
            </Grid>
          </Grid>
        )}
        <StickyFormFooter
          submitText={
            isUploadCompleted ? t('upload_completed') : t('start_upload')
          }
          disabled={isUploadCompleted}
          onSubmit={upload}
          isSubmitting={isUploading}
        />
      </Grid>
    </Paper>
  );
}

export default () => <ProductLayout main={<ProductImportImage />} />;
